import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { TemplateRef } from '@angular/core';
import { ActionIconDef, GridConfig } from '@inavista/components/grid';

// Core return order popup → Items (core items and other items tabs) on @inavista/components. Same columns, labels (red *
// on Item and Return Quantity), order, widths, alignment, Service Order links, checkbox column (once the order is past
// Open), Delete command and toolbar (Columns, Add Item while Open, Search, Excel export for AllGrid_Toolbar_Excel_Export,
// Clear filters) as the Syncfusion grids they replace. Rows are the popup's own items not deleted (LocalRowsGridApi),
// saved with the popup. Each editable cell shows its editor (CellEditorHost).

export const CORE_RETURN_ITEMS_URL = 'page://core-return-order-items';
export const CORE_RETURN_ITEMS_PERSISTENCE_KEY = 'rims.service.core-return-order-items';
export const CORE_RETURN_CORE_ITEMS_PERSISTENCE_KEY = 'rims.service.core-return-order-core-items';
export const ADD_ITEM_ACTION = 'add-item';
export const DELETE_ITEM_ACTION = 'Delete Item';
/** A hidden field the page writes when a row opens, so leaving it always saves it (the Syncfusion grid saved on leave). */
export const OPEN_FIELD = '__open';

const BLACK = '#000000de';
const PLUS_ICON = '<line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />';
const TRASH_ICON = '<polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />';

const LABELS: Record<string, string> = {
  itemSOKey: 'Item', serviceOrderId: 'Service Order', serviceOrderIdLN: 'Service Order LN', itemDescription: 'Description',
  defectiveOnHand: 'Defective On Hand', quantity: 'Return Quantity', comments: 'Comments',
};
const WIDTHS: Record<string, number> = {
  itemSOKey: 250, serviceOrderId: 150, serviceOrderIdLN: 150, itemDescription: 200, defectiveOnHand: 142, quantity: 142,
  comments: 200,
};
const TYPES: Record<string, string> = { defectiveOnHand: 'decimal', quantity: 'decimal' };

/** The schema of the shown fields, plus the row key and the open marker. */
export function coreReturnItemsSchema(fields: string[]): [string, string][] {
  return [['tempRowId', 'int'], ...fields.map(f => [f, TYPES[f] ?? 'nvarchar'] as [string, string]), [OPEN_FIELD, 'nvarchar']];
}

export function coreReturnItemsActionIcons(): ActionIconDef[] {
  // The old command: shown while the order is Open (the only time the column existed).
  return [{ key: DELETE_ITEM_ACTION, label: 'Delete Item', icon: TRASH_ICON, colorFn: () => BLACK }];
}

export interface CoreReturnItemsGridContext {
  persistenceKey: string;
  /** The grid's columns in the old order (every one has a cell editor, CellEditorHost). */
  fields: string[];
  /** Hidden and out of the column chooser (Service Order). */
  backing: string[];
  editors: Record<string, TemplateRef<unknown>>;
  cellTemplates?: Record<string, TemplateRef<unknown>>;
  /** Past Open: the old checkbox column (and no Delete, no Add Item). */
  selectable: boolean;
  canAdd: boolean;
  canExport: boolean;
  editLockReason: () => string | null;
}

export function buildCoreReturnItemsGridConfig(ctx: CoreReturnItemsGridContext): GridConfig {
  const shown = ctx.fields.filter(f => !ctx.backing.includes(f));
  return {
    apiBaseUrl: CORE_RETURN_ITEMS_URL,
    clientSide: true,
    keyColumn: 'tempRowId',
    rowHeight: 30,
    ...(ctx.selectable ? { selectable: true, selectColumnWidth: 55 } : {}),
    defaultColumnOrder: shown,
    hiddenByDefaultColumns: ['tempRowId', OPEN_FIELD, ...ctx.backing],
    // Item, Description and Return Quantity were out of the column chooser.
    alwaysVisibleColumns: ['itemSOKey', 'itemDescription', 'quantity'],
    columnLabels: Object.fromEntries(shown.map(f => [f, LABELS[f]])),
    // The old header asterisks (red, styles.scss): the marker only; the page's own checks decide on save.
    requiredColumns: ['itemSOKey', 'quantity'],
    // Also when the order is locked (the old header showed them either way).
    requiredMarkers: 'always',
    // The old alignment: quantities right, the rest left; headers left.
    columnAlign: Object.fromEntries(shown.map(f => [f, {
      horizontal: TYPES[f] ? 'right' as const : 'left' as const, header: { horizontal: 'left' as const },
    }])),
    // The old widths; Description and Comments had none and share the space left.
    columnWidths: Object.fromEntries(shown.map(f => [f, WIDTHS[f]])),
    stretchColumns: true,
    columnSizing: Object.fromEntries(shown.filter(f => f !== 'itemDescription' && f !== 'comments').map(f => [f, { noStretch: true }])),
    // Syncfusion's 'n': grouped, up to 3 decimals.
    columnNumberFormats: { defectiveOnHand: { maximumFractionDigits: 3 }, quantity: { maximumFractionDigits: 3 } },
    // The Service Order columns open the service order, as before.
    hyperlinkColumns: ['serviceOrderIdLN', 'serviceOrderId'].filter(f => shown.includes(f)),
    cellTemplates: ctx.cellTemplates ?? {},
    // Item and the Service Order columns had the Excel (checkbox list) filter.
    excelFilterColumns: ['itemSOKey', 'serviceOrderIdLN', 'serviceOrderId'].filter(f => shown.includes(f)),
    // The old Actions column was 130 wide.
    actionsColumnMinWidth: 130,
    // Rows open only for a user who may change them, as the old editSettings.allowEditing (the page rebuilds
    // the config when that changes): a row whose every cell is locked would still open, with nothing to edit.
    editMode: ctx.editLockReason() ? 'none' : 'inline',
    editSaveMode: 'row',
    // A click on a cell opens its row, as the old grid's mouseup handler did.
    editTrigger: 'click',
    editableColumns: ctx.fields,
    editorTemplates: ctx.editors,
    readOnlyCellFn: () => !!ctx.editLockReason(),
    deleteMode: 'host',
    rowDeletable: () => false,
    toolbarActions: ctx.canAdd ? [{ key: ADD_ITEM_ACTION, label: 'Add Item', kind: 'button', icon: PLUS_ICON }] : [],
    enableColumnsPicker: true,
    enableExport: ctx.canExport,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    multiSort: true,
    emptyStateMessage: 'No records to display',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: ctx.persistenceKey,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
